import type { UUID } from '@cobliteam/common-types';

import { GraphQLUserAlertSettingsRequester } from '@cobliteam/api-lib';
import { getDefaultSetupSettings } from '@cobliteam/api-lib/src/dashboard/userAlert/settings/definitions';

import { Environment } from '@cobliteam/react-environments';

declare const OneSignal: any; // OneSignal global variable initialized on index.html

let hasBeenInitialized = false;

export class Notifications {
  public static isInitialized = () =>
    hasBeenInitialized && OneSignal.initialized;

  public static initialize = (
    fleetId: UUID,
    fleetName: string,
    userId: UUID
  ) => {
    if (hasBeenInitialized) {
      console.warn('Notifications has already been initialized');
      Notifications.setUser(fleetId, fleetName, userId);
      return;
    }

    const { oneSignalAppId } = Environment.getInstance().getEnvironment();

    if (oneSignalAppId) {
      const initOptions = { appId: oneSignalAppId };
      OneSignal.push(() => {
        OneSignal.init(initOptions);
      });

      OneSignal.push(() => {
        OneSignal.on('popoverAllowClick', () => {
          const userAlertSettingsRequester =
            new GraphQLUserAlertSettingsRequester();
          userAlertSettingsRequester.setupSettings(getDefaultSetupSettings());
        });
      });

      Notifications.setUser(fleetId, fleetName, userId);
    }

    hasBeenInitialized = true;
  };

  public static setUser = (fleetId: UUID, fleetName: string, userId: UUID) => {
    OneSignal.push(() => {
      OneSignal.setExternalUserId(userId);

      const tags = {
        fleet_id: fleetId,
        fleet_name: fleetName,
      };
      OneSignal.sendTags(tags);
    });
  };

  public static isActive = (): Promise<boolean> =>
    OneSignal.isPushNotificationsEnabled();

  public static getBrowserPermission = (callback: () => void): Promise<void> =>
    OneSignal.push([
      'getNotificationPermission',
      (permission: any) => {
        if (permission === 'granted') {
          callback();
        }
      },
    ]);

  public static updateSubscription = (
    active: boolean,
    callback: () => void
  ): Promise<void> => {
    if (active) {
      OneSignal.showNativePrompt();
    }
    return Notifications.getBrowserPermission(callback).then(
      OneSignal.setSubscription(active)
    );
  };
}
